import authApiService from "./userApiService"; // Ensure this is your axios instance with interceptors

// Helper function to handle errors
const handleError = (error) => {
  console.error('API Error:', error);
  return {
    success: false,
    error: error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred',
    statusCode: error.response?.status,
    details: error.response?.data?.details || null
  };
};

// Helper function to handle successful responses
const handleSuccess = (response, message = null) => {
  return {
    success: true,
    data: response.data,
    message: message || response.data?.message || 'Operation completed successfully',
    statusCode: response.status
  };
};

export const pettyCashService = {
  // ============================================================================
  // TRANSACTION CRUD OPERATIONS (RESTful)
  // ============================================================================

  // Get all transactions
  async getAllTransactions() {
    try {
      const response = await authApiService.get("/petty-cash/transactions");
      return handleSuccess(response, 'Petty cash transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction by ID
  async getTransactionById(id) {
    try {
      const response = await authApiService.get(`/petty-cash/transactions/${id}`);
      return handleSuccess(response, 'Petty cash transaction retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Add new transaction
  async addTransaction(transactionData) {
    try {
      const payload = {
        transactionType: transactionData.transactionType,
        amount: parseFloat(transactionData.amount),
        notes: transactionData.notes || '',
        name: transactionData.name || '',
        department: transactionData.department || '',
        receipts: transactionData.receipts !== undefined ? transactionData.receipts : [],
      };

      const response = await authApiService.post("/petty-cash/transactions", payload);
      return handleSuccess(response, 'Petty cash transaction added successfully');
    } catch (error) {
      return handleError(error);
    }
  },
  async getEmployees() {
    try {
      const response = await authApiService.get("/petty-cash/employees");
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },
  // Update transaction by ID
  async updateTransaction(id, transactionData) {
    try {
      const payload = {
        date: transactionData.date,
        name: transactionData.name,
        notes: transactionData.notes,
        department: transactionData.department || '',
      };

      if (transactionData.receipts !== undefined) {
        payload.receipts = transactionData.receipts;
      }

      const response = await authApiService.put(`/petty-cash/transactions/${id}`, payload);
      return handleSuccess(response, 'Petty cash transaction updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete transaction by ID
  async deleteTransaction(id) {
    try {
      const response = await authApiService.delete(`/petty-cash/transactions/${id}`);
      return handleSuccess(response, 'Petty cash transaction deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // BALANCE OPERATIONS
  // ============================================================================

  // Get current balance
  async getCurrentBalance() {
    try {
      const response = await authApiService.get("/petty-cash/balance");
      return handleSuccess(response, 'Petty cash balance retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // VERIFICATION OPERATIONS
  // ============================================================================

  // Verify balance (Admin/Management only)
  async verifyBalance(verificationData) {
    try {
      const payload = {
        pettyCashAmount: parseFloat(verificationData.pettyCashAmount)
      };

      const response = await authApiService.post("/petty-cash/verify", payload);
      return handleSuccess(response, 'Petty cash balance verification completed');
    } catch (error) {
      return handleError(error);
    }
  },

  // Check verification status
  async checkVerificationStatus() {
    try {
      const response = await authApiService.get("/petty-cash/verification-status");
      return handleSuccess(response, 'Petty cash verification status retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  // Verify specific transaction (Admin/Management only)
  async verifySpecificTransaction(id, verificationData) {
    try {
      const payload = {
        pettyCashAmount: parseFloat(verificationData.pettyCashAmount)
      };

      const response = await authApiService.post(`/petty-cash/transactions/${id}/verify`, payload);
      return handleSuccess(response, 'Petty cash transaction verification completed');
    } catch (error) {
      return handleError(error);
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await authApiService.get("/petty-cash/health");
      return handleSuccess(response, 'Petty cash service is healthy');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // LEGACY/BACKWARD COMPATIBILITY METHODS
  // ============================================================================

  // Alternative method using traditional route
  async getTransactions() {
    try {
      const response = await authApiService.get("/petty-cash/get-transactions");
      return handleSuccess(response, 'Petty cash transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async getBalance() {
    try {
      const response = await authApiService.get("/petty-cash/get-balance");
      return handleSuccess(response, 'Petty cash balance retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async createTransaction(transactionData) {
    try {
      const payload = {
        transactionType: transactionData.transactionType,
        amount: parseFloat(transactionData.amount),
        notes: transactionData.notes || '',
        name: transactionData.name || '',
        department: transactionData.department || '',
      };

      const response = await authApiService.post("/petty-cash/add-transaction", payload);
      return handleSuccess(response, 'Petty cash transaction created successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async editTransaction(transactionData) {
    try {
      const payload = {
        id: transactionData.id,
        date: transactionData.date,
        name: transactionData.name,
        notes: transactionData.notes
      };

      const response = await authApiService.put(`/petty-cash/edit-transaction/${transactionData.id}`, payload);
      return handleSuccess(response, 'Petty cash transaction edited successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async removeTransaction(id) {
    try {
      const response = await authApiService.delete(`/petty-cash/delete-transaction/${id}`);
      return handleSuccess(response, 'Petty cash transaction removed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async verifyPettyCashBalance(verificationData) {
    try {
      const payload = {
        pettyCashAmount: parseFloat(verificationData.pettyCashAmount)
      };

      const response = await authApiService.post("/petty-cash/verify-balance", payload);
      return handleSuccess(response, 'Petty cash balance verified successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Alternative method using traditional route
  async checkVerification() {
    try {
      const response = await authApiService.get("/petty-cash/check-verification");
      return handleSuccess(response, 'Petty cash verification status checked');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // ADVANCED OPERATIONS
  // ============================================================================

  // Batch operations for multiple transactions
  async batchDeleteTransactions(transactionIds) {
    try {
      const deletePromises = transactionIds.map(id => this.deleteTransaction(id));
      const results = await Promise.allSettled(deletePromises);
      
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success);
      const failed = results.filter(result => result.status === 'rejected' || !result.value.success);

      return {
        success: true,
        data: {
          totalProcessed: transactionIds.length,
          successful: successful.length,
          failed: failed.length,
          successfulIds: successful.map((_, index) => transactionIds[index]),
          failedIds: failed.map((_, index) => transactionIds[index]),
          results: results
        },
        message: `Batch delete completed: ${successful.length} successful, ${failed.length} failed`
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // Batch operations for multiple transaction updates
  async batchUpdateTransactions(transactionUpdates) {
    try {
      const updatePromises = transactionUpdates.map(update => 
        this.updateTransaction(update.id, update.data)
      );
      const results = await Promise.allSettled(updatePromises);
      
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success);
      const failed = results.filter(result => result.status === 'rejected' || !result.value.success);

      return {
        success: true,
        data: {
          totalProcessed: transactionUpdates.length,
          successful: successful.length,
          failed: failed.length,
          results: results
        },
        message: `Batch update completed: ${successful.length} successful, ${failed.length} failed`
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transactions with filters and pagination
  async getFilteredTransactions(filters = {}, pagination = {}) {
    try {
      const params = {};
      
      // Filter parameters
      if (filters.transactionType) params.transactionType = filters.transactionType;
      if (filters.name) params.name = filters.name;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.addedBy) params.addedBy = filters.addedBy;
      if (filters.minAmount) params.minAmount = filters.minAmount;
      if (filters.maxAmount) params.maxAmount = filters.maxAmount;
      
      // Pagination parameters
      if (pagination.page) params.page = pagination.page;
      if (pagination.limit) params.limit = pagination.limit;
      if (pagination.sortBy) params.sortBy = pagination.sortBy;
      if (pagination.sortOrder) params.sortOrder = pagination.sortOrder;

      const response = await authApiService.get("/petty-cash/transactions", { params });
      return handleSuccess(response, 'Filtered petty cash transactions retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction summary/statistics
  async getTransactionSummary(period = 'daily', filters = {}) {
    try {
      const params = { period, ...filters };
      const response = await authApiService.get("/petty-cash/transactions/summary", { params });
      return handleSuccess(response, 'Petty cash transaction summary retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction analytics
  async getTransactionAnalytics(dateRange = {}, groupBy = 'day') {
    try {
      const params = {
        groupBy,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      const response = await authApiService.get("/petty-cash/transactions/analytics", { params });
      return handleSuccess(response, 'Petty cash transaction analytics retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Export transactions
  async exportTransactions(format = 'csv', filters = {}) {
    try {
      const params = { format, ...filters };
      const response = await authApiService.get("/petty-cash/transactions/export", { 
        params,
        responseType: 'blob' // For file downloads
      });
      return { 
        success: true, 
        data: response.data, 
        headers: response.headers,
        message: 'Petty cash transactions exported successfully'
      };
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  // Validate transaction data before sending
  validateTransactionData(transactionData) {
    const errors = [];
    
    if (!transactionData.transactionType) {
      errors.push('Transaction type is required');
    } else if (!['deposit', 'withdraw'].includes(transactionData.transactionType)) {
      errors.push('Transaction type must be either "deposit" or "withdraw"');
    }
    
    if (!transactionData.amount) {
      errors.push('Amount is required');
    } else if (isNaN(parseFloat(transactionData.amount))) {
      errors.push('Amount must be a valid number');
    } else if (parseFloat(transactionData.amount) <= 0) {
      errors.push('Amount must be greater than 0');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  // Format transaction data for API
  formatTransactionData(transactionData) {
    return {
      transactionType: transactionData.transactionType,
      amount: parseFloat(transactionData.amount),
      notes: transactionData.notes?.trim() || '',
      name: transactionData.name?.trim() || '',
      date: transactionData.date || new Date().toISOString()
    };
  },

  // Calculate transaction totals
  calculateTransactionTotals(transactions) {
    return transactions.reduce((totals, transaction) => {
      const amount = parseFloat(transaction.transaction_amount || 0);
      
      if (transaction.transaction_type === 'deposit') {
        totals.totalDeposits += amount;
        totals.depositCount += 1;
      } else if (transaction.transaction_type === 'withdraw') {
        totals.totalWithdrawals += amount;
        totals.withdrawalCount += 1;
      }
      
      return totals;
    }, {
      totalDeposits: 0,
      totalWithdrawals: 0,
      depositCount: 0,
      withdrawalCount: 0,
      netAmount: 0
    });
  },

  // Search transactions by multiple criteria
  async searchTransactions(searchCriteria) {
    try {
      const params = {
        search: searchCriteria.query,
        searchFields: searchCriteria.fields?.join(',') || 'name,notes,added_by',
        ...searchCriteria.filters
      };

      const response = await authApiService.get("/petty-cash/transactions/search", { params });
      return handleSuccess(response, 'Petty cash search completed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transaction history for a specific person/name
  async getPersonTransactionHistory(name, filters = {}) {
    try {
      const params = {
        name: name,
        ...filters
      };

      const response = await authApiService.get("/petty-cash/transactions/person-history", { params });
      return handleSuccess(response, 'Person transaction history retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // PETTY CASH SPECIFIC OPERATIONS
  // ============================================================================

  // Check if current time is within allowed transaction window
  async checkTimeWindow() {
    try {
      const response = await authApiService.get("/petty-cash/time-window-status");
      return handleSuccess(response, 'Time window status retrieved');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get daily cash verification report
  async getDailyVerificationReport(date = null) {
    try {
      const params = date ? { date } : {};
      const response = await authApiService.get("/petty-cash/daily-verification-report", { params });
      return handleSuccess(response, 'Daily verification report retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get cash flow summary
  async getCashFlowSummary(startDate, endDate) {
    try {
      const params = { startDate, endDate };
      const response = await authApiService.get("/petty-cash/cash-flow-summary", { params });
      return handleSuccess(response, 'Cash flow summary retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get balance history
  async getBalanceHistory(period = 'week') {
    try {
      const params = { period };
      const response = await authApiService.get("/petty-cash/balance-history", { params });
      return handleSuccess(response, 'Balance history retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get transactions by date range
  async getTransactionsByDateRange(startDate, endDate) {
    try {
      const params = { startDate, endDate };
      const response = await authApiService.get("/petty-cash/transactions/date-range", { params });
      return handleSuccess(response, 'Transactions by date range retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Reconcile petty cash (compare physical cash with system balance)
  async reconcilePettyCash(physicalCashAmount, reconciliationNotes = '') {
    try {
      const payload = {
        physicalCashAmount: parseFloat(physicalCashAmount),
        reconciliationNotes: reconciliationNotes.trim(),
        reconciliationDate: new Date().toISOString()
      };

      const response = await authApiService.post("/petty-cash/reconcile", payload);
      return handleSuccess(response, 'Petty cash reconciliation completed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get reconciliation history
  async getReconciliationHistory() {
    try {
      const response = await authApiService.get("/petty-cash/reconciliation-history");
      return handleSuccess(response, 'Reconciliation history retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // REPORTING METHODS
  // ============================================================================

  // Generate petty cash report
  async generateReport(reportType = 'summary', filters = {}) {
    try {
      const params = { reportType, ...filters };
      const response = await authApiService.get("/petty-cash/reports", { params });
      return handleSuccess(response, 'Petty cash report generated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get low balance alerts configuration
  async getLowBalanceConfig() {
    try {
      const response = await authApiService.get("/petty-cash/low-balance-config");
      return handleSuccess(response, 'Low balance configuration retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update low balance alerts configuration
  async updateLowBalanceConfig(threshold, alertEnabled = true) {
    try {
      const payload = {
        threshold: parseFloat(threshold),
        alertEnabled: alertEnabled
      };

      const response = await authApiService.put("/petty-cash/low-balance-config", payload);
      return handleSuccess(response, 'Low balance configuration updated successfully');
    } catch (error) {
      return handleError(error);
    }
  }
};

// Default export for convenience
export default pettyCashService;